import { logger } from '../utils/logger';
import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { motion, AnimatePresence } from 'framer-motion';
import { LogOut, Menu, X, Utensils, ShoppingBag, Tag, MessageSquare, LayoutDashboard, Calendar } from 'lucide-react';
import { Button } from './ui/button';
import axios from '../api/axios';

const AdminNavbar = () => {
  const { admin, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const handleLogout = () => {
    logout();
    navigate('/admin/login');
  };

  // Sound for notifications (Simple beep)
  const notificationSound = new Audio("data:audio/wav;base64,UklGRl9vT19XQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YU..."); // Placeholder, will use a real short beep base64 in actual call

  const [pendingOrdersCount, setPendingOrdersCount] = useState(0);
  const firstLoad = React.useRef(true);

  // Request Notification Permission
  React.useEffect(() => {
    if ('Notification' in window && Notification.permission !== 'granted') {
      Notification.requestPermission();
    }
  }, []);

  // Poll for new orders
  React.useEffect(() => {
    const checkNewOrders = async () => {
      try {
        const response = await axios.get('/orders?status=pending');
        const currentCount = response.data.length;

        if (!firstLoad.current && currentCount > pendingOrdersCount) {
          // New order detected!
          const audio = new Audio("https://actions.google.com/sounds/v1/alarms/beep_short.ogg");
          audio.play().catch(() => { });

          if ('Notification' in window && Notification.permission === 'granted') {
            new Notification('New Order Received!', {
              body: `You have ${currentCount} pending orders.`,
              icon: '/logo.jpg'
            });
          }

          // Visual alert via standard browser alert (as requested for "forced")
          // But browser alert blocks thread. Let's rely on Sound + Notification API + red badge.
        }

        setPendingOrdersCount(currentCount);
        firstLoad.current = false;
      } catch (error) {
        logger.error('Error polling orders:', error);
      }
    };

    // Initial check
    checkNewOrders();

    // Poll every 15 seconds
    const interval = setInterval(checkNewOrders, 15000);

    return () => clearInterval(interval);
  }, [pendingOrdersCount]);

  const navItems = [
    { name: 'Dashboard', path: '/admin/dashboard', icon: LayoutDashboard },
    { name: 'Menu', path: '/admin/menu', icon: Utensils },
    {
      name: 'Orders',
      path: '/admin/orders',
      icon: ShoppingBag,
      badge: pendingOrdersCount > 0 ? pendingOrdersCount : null
    },
    { name: 'Deals', path: '/admin/deals', icon: Tag },
    { name: 'Events', path: '/admin/events', icon: Calendar },
    { name: 'Messages', path: '/admin/messages', icon: MessageSquare },
  ];

  if (!admin) return null;

  return (
    <nav className="bg-black/80 backdrop-blur-md border-b border-gold-500/20 sticky top-0 z-50">
      <div className="container mx-auto px-4">
        <div className="flex justify-between items-center h-16">
          {/* Logo and Title */}
          <Link to="/admin/dashboard" className="flex items-center space-x-3 group">
            <div className="relative w-10 h-10 overflow-hidden rounded-full border-2 border-gold-500/50 group-hover:border-gold-500 transition-colors">
              <img
                src="/logo.jpg"
                alt="Magic World Logo"
                className="w-full h-full object-cover transform group-hover:scale-110 transition-transform duration-500"
              />
            </div>
            <span className="text-xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-gold-400 to-gold-200">
              Admin Panel
            </span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-1">
            {navItems.map((item) => {
              const isActive = location.pathname === item.path;
              const Icon = item.icon;
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`relative px-4 py-2 rounded-lg text-sm font-medium transition-all duration-300 flex items-center gap-2 group ${isActive ? 'text-gold-400 bg-gold-500/10' : 'text-gray-400 hover:text-gold-200 hover:bg-white/5'
                    }`}
                >
                  <Icon className={`w-4 h-4 ${isActive ? 'text-gold-500' : 'text-gray-500 group-hover:text-gold-400'}`} />
                  {item.name}
                  {item.badge > 0 && (
                    <span className="ml-1 bg-red-500 text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full min-w-[1.2rem] text-center">
                      {item.badge}
                    </span>
                  )}
                  {isActive && (
                    <motion.div
                      layoutId="navbar-indicator"
                      className="absolute bottom-0 left-0 right-0 h-0.5 bg-gold-500 rounded-full"
                      initial={false}
                      transition={{ type: "spring", stiffness: 500, damping: 30 }}
                    />
                  )}
                </Link>
              );
            })}
          </div>

          <div className="hidden md:flex items-center gap-4">
            <div className="text-sm">
              <span className="text-gray-500">Signed in as </span>
              <span className="text-gold-400 font-semibold">{admin.username}</span>
            </div>
            <Button
              variant="destructive"
              size="sm"
              onClick={handleLogout}
              className="gap-2 bg-red-500/10 text-red-500 hover:bg-red-500/20 hover:text-red-400 border border-red-500/20"
            >
              <LogOut className="w-4 h-4" />
              Logout
            </Button>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="md:hidden p-2 text-gray-400 hover:text-white"
          >
            {isMobileMenuOpen ? <X /> : <Menu />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="md:hidden overflow-hidden bg-black/95 border-b border-gold-500/20"
          >
            <div className="px-4 py-4 space-y-2">
              {navItems.map((item) => {
                const isActive = location.pathname === item.path;
                const Icon = item.icon;
                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    onClick={() => setIsMobileMenuOpen(false)}
                    className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${isActive ? 'bg-gold-500/20 text-gold-400' : 'text-gray-400 hover:bg-white/5 hover:text-white'
                      }`}
                  >
                    <Icon className="w-5 h-5" />
                    <span className="flex-1">{item.name}</span>
                    {item.badge > 0 && (
                      <span className="bg-red-500 text-white text-xs font-bold px-2 py-0.5 rounded-full">
                        {item.badge}
                      </span>
                    )}
                  </Link>
                );
              })}
              <div className="pt-4 border-t border-white/10 mt-4">
                <div className="flex items-center justify-between px-4 mb-4">
                  <span className="text-sm text-gray-500">Signed in as {admin.username}</span>
                </div>
                <Button
                  variant="destructive"
                  className="w-full gap-2"
                  onClick={handleLogout}
                >
                  <LogOut className="w-4 h-4" />
                  Logout
                </Button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </nav>
  );
};

export default AdminNavbar;

